import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Baby, Bell, Trash, FileText, ShieldCheck, FloppyDisk, Check, UserCircle, SignOut, SignIn } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import { BIRTH_TERMS, AVATAR_DEFAULT, ILLUSTRATIONS } from "../data/staticData";
import { getJSON, setJSON, remove, clearAll, K } from "../lib/storage";
import { useAuth } from "../lib/auth";

const AVATAR_OPTIONS = [AVATAR_DEFAULT, ...ILLUSTRATIONS.map((i) => i.src)];

export default function Profil() {
  const { user, logout, syncProfile, deleteServerAccount } = useAuth();
  const stored = getJSON(K.profile, {});
  const [name, setName] = useState(stored.name || "");
  const [dob, setDob] = useState(stored.baby_birth_date || "");
  const [term, setTerm] = useState(stored.term ?? null);
  const [avatar, setAvatar] = useState(stored.avatar || AVATAR_DEFAULT);
  const [saved, setSaved] = useState(false);
  const [reminder, setReminder] = useState(() => getJSON(K.reminder, false));

  const saveProfile = () => {
    setJSON(K.profile, { name: name.trim(), baby_birth_date: dob, term, avatar });
    if (user) syncProfile(name.trim(), term);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const pickAvatar = (src) => {
    setAvatar(src);
    setJSON(K.profile, { ...getJSON(K.profile, {}), avatar: src });
  };

  const pickTerm = (weeks) => setTerm(term === weeks ? null : weeks);

  const toggleReminder = () => {
    const next = !reminder;
    setReminder(next);
    setJSON(K.reminder, next);
  };

  const clearMoods = () => {
    if (window.confirm("Effacer définitivement tout votre historique d'humeurs sur cet appareil ?")) {
      remove(K.moods);
      window.alert("Vos humeurs ont été effacées.");
    }
  };

  const clearEpds = () => {
    if (window.confirm("Effacer définitivement tous vos résultats du test d'Édimbourg ?")) {
      remove(K.epds);
      window.alert("Vos résultats EPDS ont été effacés.");
    }
  };

  const deleteEverything = () => {
    if (
      window.confirm(
        "Supprimer TOUTES vos données Renaître (profil, humeurs, résultats EPDS, consentement) ? Cette action est irréversible (RGPD Art. 17)."
      )
    ) {
      clearAll();
      window.location.href = "/";
    }
  };

  return (
    <div>
      <PageHeader title="Profil" />

      <div className="px-6">
        {/* User card */}
        <section className="mb-4 rounded-[26px] border border-[#36302B]/6 bg-white p-5 shadow-[0_4px_20px_rgba(54,48,43,0.03)]">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 overflow-hidden rounded-full border-2 border-[#F2EDE4] shadow-sm">
              <img src={avatar} alt="Avatar" className="h-full w-full object-cover" data-testid="profile-avatar" />
            </div>
            <div className="flex-1">
              <p className="font-display text-[18px] font-medium text-[#36302B]">{name || "Maman"}</p>
              <p className="text-[13px] text-[#36302B]/55">{user ? user.email : "Bienvenue dans votre refuge"}</p>
            </div>
          </div>

          {/* Avatar chooser */}
          <p className="mt-4 mb-2 text-[12.5px] font-semibold text-[#36302B]/55">Choisir mon personnage</p>
          <div className="flex flex-wrap gap-2">
            {AVATAR_OPTIONS.map((src, i) => (
              <button
                key={i}
                data-testid={`avatar-option-${i}`}
                onClick={() => pickAvatar(src)}
                className="h-12 w-12 overflow-hidden rounded-full transition-all"
                style={{
                  boxShadow: avatar === src ? "0 0 0 3px #C57B67" : "0 0 0 1px rgba(54,48,43,0.1)",
                }}
              >
                <img src={src} alt={`Avatar ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </section>

        {/* Account (optional) */}
        <section className="mb-4 rounded-[26px] border border-[#36302B]/6 bg-white p-5">
          <div className="mb-2 flex items-center gap-2">
            <UserCircle size={22} color="#9CAEA9" weight="duotone" />
            <h2 className="font-display text-lg font-medium text-[#36302B]">Mon compte</h2>
          </div>
          {user ? (
            <>
              <p className="text-[13.5px] leading-relaxed text-[#36302B]/65">
                Connectée en tant que <strong>{user.email}</strong>. Seuls votre prénom et le terme de bébé sont
                synchronisés. Vos humeurs et l'EPDS restent sur cet appareil.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  data-testid="account-logout"
                  onClick={logout}
                  className="flex items-center justify-center gap-2 rounded-full border border-[#36302B]/10 py-3 text-[14px] font-bold text-[#36302B]"
                >
                  <SignOut size={18} /> Se déconnecter
                </button>
                <button
                  data-testid="account-delete-server"
                  onClick={() => {
                    if (window.confirm("Supprimer définitivement votre compte serveur (email, prénom, terme) ? Vos données locales ne sont pas affectées. (RGPD Art. 17)")) {
                      deleteServerAccount();
                      window.alert("Votre compte a été supprimé.");
                    }
                  }}
                  className="flex items-center justify-center gap-2 rounded-full border border-[#B56A63]/30 py-3 text-[14px] font-bold text-[#B56A63]"
                >
                  <Trash size={18} /> Supprimer mon compte
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13.5px] leading-relaxed text-[#36302B]/65">
                Facultatif. Créez un compte pour retrouver votre prénom et le terme de bébé sur un autre
                appareil. L'app fonctionne parfaitement sans compte.
              </p>
              <div className="mt-3 flex gap-2">
                <Link
                  to="/login"
                  data-testid="account-login"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#9CAEA9] py-3 text-[14px] font-bold text-white"
                >
                  <SignIn size={18} /> Se connecter
                </Link>
                <Link
                  to="/register"
                  data-testid="account-register"
                  className="flex flex-1 items-center justify-center rounded-full border border-[#36302B]/12 py-3 text-[14px] font-bold text-[#36302B]"
                >
                  Créer un compte
                </Link>
              </div>
            </>
          )}
        </section>

        {/* Baby */}
        <section className="rounded-[26px] border border-[#36302B]/6 bg-white p-6 shadow-[0_4px_20px_rgba(54,48,43,0.03)]">
          <div className="mb-4 flex items-center gap-2">
            <Baby size={22} color="#C57B67" weight="duotone" />
            <h2 className="font-display text-lg font-medium text-[#36302B]">Vous & bébé</h2>
          </div>

          <label className="text-[13px] font-semibold text-[#36302B]/60">Votre prénom</label>
          <input
            data-testid="profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Comment vous appelez-vous ?"
            className="mt-1 mb-4 w-full rounded-2xl border border-[#36302B]/10 bg-[#FAF8F5] px-4 py-3 text-[15px] outline-none focus:border-[#C57B67]"
          />

          <label className="text-[13px] font-semibold text-[#36302B]/60">Date de naissance de bébé</label>
          <input
            type="date"
            data-testid="profile-dob"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            className="mt-1 mb-4 w-full rounded-2xl border border-[#36302B]/10 bg-[#FAF8F5] px-4 py-3 text-[15px] outline-none focus:border-[#C57B67]"
          />

          <label className="text-[13px] font-semibold text-[#36302B]/60">Terme de naissance</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {BIRTH_TERMS.map((b) => (
              <button
                key={b.weeks}
                data-testid={`profile-term-${b.weeks}`}
                onClick={() => pickTerm(b.weeks)}
                className="rounded-full px-4 py-2 text-[13px] font-bold transition-colors"
                style={{
                  backgroundColor: term === b.weeks ? "#C57B67" : "#FAF8F5",
                  color: term === b.weeks ? "#FFFFFF" : "rgba(54,48,43,0.6)",
                  border: "1px solid rgba(54,48,43,0.1)",
                }}
              >
                {b.label}
              </button>
            ))}
          </div>

          <button
            data-testid="profile-save"
            onClick={saveProfile}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#C57B67] py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-[#B56A63]"
          >
            {saved ? <Check size={18} weight="bold" /> : <FloppyDisk size={18} weight="fill" />}
            {saved ? "Enregistré" : "Enregistrer"}
          </button>
        </section>

        {/* Reminder */}
        <section className="mt-4 flex items-center gap-3 rounded-[26px] border border-[#36302B]/6 bg-white p-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#9CAEA9]/18">
            <Bell size={22} color="#88A096" weight="duotone" />
          </div>
          <div className="flex-1">
            <p className="font-display text-[15px] font-medium text-[#36302B]">Rappel doux du matin</p>
            <p className="text-[12.5px] text-[#36302B]/55">Bientôt disponible sur le web</p>
          </div>
          <button
            data-testid="profile-reminder-toggle"
            onClick={toggleReminder}
            className="relative h-7 w-12 rounded-full transition-colors"
            style={{ backgroundColor: reminder ? "#9CAEA9" : "rgba(54,48,43,0.15)" }}
            aria-label="Basculer le rappel"
          >
            <span
              className="absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all"
              style={{ left: reminder ? 26 : 4 }}
            />
          </button>
        </section>

        {/* Privacy & data */}
        <section className="mt-4 rounded-[26px] border border-[#36302B]/6 bg-white p-6">
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck size={22} color="#88A096" weight="duotone" />
            <h2 className="font-display text-lg font-medium text-[#36302B]">Confidentialité & données</h2>
          </div>
          <p className="mb-4 text-[13px] leading-relaxed text-[#36302B]/60">
            Vos données de santé sont stockées uniquement sur cet appareil. Vous gardez le contrôle total.
          </p>

          <button
            data-testid="profile-clear-moods"
            onClick={clearMoods}
            className="mb-2 flex w-full items-center gap-2 rounded-2xl border border-[#36302B]/10 px-4 py-3 text-[14px] font-semibold text-[#36302B]"
          >
            <Trash size={18} /> Effacer mon historique d'humeurs
          </button>
          <button
            data-testid="profile-clear-epds"
            onClick={clearEpds}
            className="flex w-full items-center gap-2 rounded-2xl border border-[#36302B]/10 px-4 py-3 text-[14px] font-semibold text-[#36302B]"
          >
            <Trash size={18} /> Effacer mes résultats EPDS
          </button>

          <button
            data-testid="profile-delete-all"
            onClick={deleteEverything}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#B56A63] py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-[#a35a54]"
          >
            <Trash size={18} weight="fill" /> Supprimer toutes mes données
          </button>
          <p className="mt-2 text-center text-[11.5px] text-[#36302B]/45">Conforme au RGPD (Article 17 — droit à l'effacement)</p>
        </section>

        {/* Legal links */}
        <section className="mt-4 overflow-hidden rounded-[26px] border border-[#36302B]/6 bg-white">
          <Link to="/privacy-policy" data-testid="profile-privacy-link" className="flex items-center gap-3 border-b border-[#36302B]/6 px-5 py-4">
            <ShieldCheck size={20} color="#C57B67" />
            <span className="flex-1 text-[14.5px] font-semibold text-[#36302B]">Politique de confidentialité</span>
          </Link>
          <Link to="/mentions-legales" data-testid="profile-legal-link" className="flex items-center gap-3 px-5 py-4">
            <FileText size={20} color="#C57B67" />
            <span className="flex-1 text-[14.5px] font-semibold text-[#36302B]">Mentions légales</span>
          </Link>
        </section>

        <p className="mt-6 text-center text-[12px] text-[#36302B]/40">
          Renaître est un outil de bien-être et ne remplace pas un avis médical.
        </p>
      </div>
    </div>
  );
}
